import { ApplicationContext, ConfigurationsContext } from '../../../App';
import { ConfigurationProps } from '../../Configuration';
import { Scroll } from '../../General';
import StackScene from '../../three/StackScene';
import { VisualizerProps } from '../../three/utils';
import {
	wrapChangeEventNumber,
	render_length,
	render_weight,
	getChangeWrapperString,
} from '../../shared/shared';
import ContentItem, {
	ButtonProps,
	ContentFooter,
	ContentItemProps,
	useConfirm,
} from '../ContentItem';
import {
	SummaryItemCell,
	SummaryScroll,
	ItemIterator,
	duplicateConfiguration,
} from '../Shared';
import LayerImage from '../images/pallet-layers.png';
import {
	Pallet_t,
	Stack as StackObject,
	PalletConfiguration,
	Layer_t,
	SlipSheetOption,
	MachineConfig,
	PalletConfigObj,
} from 'blackbox';
import { Application } from 'libapi';
import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
	StackStatsProps,
	StackMode,
	StackStep,
	AutoStackParams,
	ManualStackProps,
	AutoStackProps,
	StackCellProps,
	TargetPalletProps,
} from './stackTypes';
import {
	getSlipSheetHeight,
	createDefaultAutoStackParams,
} from './stackUtils';
import AutoStack from './AutoStack';
import ManualStack from './ManualStack';
import { useStackEditor } from './useStackEditor';

function StackStats({
	editingStack,
	allBoxes,
	allSlipSheets,
	maxBoxes,
	allPallets,
	palletIndex,
	unitsSystem,
}: StackStatsProps) {
	const { stats, boxCount } = (function () {
		let height = 0,
			boxes = 0,
			weight = 0,
			layers = 0;
		editingStack.layers.forEach(function (pLayer: Layer_t, pIdx: number) {
			if (!allPallets[palletIndex].Layouts?.[pLayer.index]) return;
			const lLayout = allPallets[palletIndex].Layouts[pLayer.index];
			layers++;
			let lBoxCnt = 0;
			for (const bPos of lLayout.boxPositions) {
				lBoxCnt += (bPos.multipick || 0) + 1;
			}
			boxes += lBoxCnt;
			height += allBoxes[lLayout.boxPositions[0].index].dimensions.height;
			height += getSlipSheetHeight(
				pIdx,
				editingStack.layers.length,
				allSlipSheets,
				editingStack.slipSheet
			);
			weight +=
				(allBoxes[lLayout.boxPositions[0].index].weight || 0) *
				lLayout.boxPositions.length;
		});
		// Top slipsheet
		height += getSlipSheetHeight(
			editingStack.layers.length,
			editingStack.layers.length,
			allSlipSheets,
			editingStack.slipSheet
		);
		return {
			stats: [
				['Pallet', allPallets[palletIndex].name],
				['Layers', String(layers)],
				['Boxes', String(boxes)],
				['Height', render_length(height, unitsSystem, true, 1)],
				['Weight', render_weight(weight, unitsSystem, true, 1)],
			],
			boxCount: boxes,
		};
	})();

	return (
		<div className="full-size flex-col justify-center align-center">
			{maxBoxes && boxCount > maxBoxes && (
				<span className="half-padding font-normal color-red">
					Box count exceeds display limit. Partial Visualization Shown.
				</span>
			)}

			<table className="half-padding min-width-90 border">
				<tbody>
					{stats.map(function (stat, statIndex) {
						const [lTitle, lValue] = stat;
						return (
							<tr key={statIndex}>
								<td className="half-padding-top">
									<b> {lTitle}</b>
								</td>
								<td className="half-padding-top"> {lValue}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

export default function Stack({
	instructionNumber,
	allPallets,
	allSlipSheets,
	allBoxes,
	setPallets,
	handleBack,
	handleNext,
	unitsSystem,
	triggerKeyboard,
	triggerSelect,
}: ConfigurationProps) {
	interface _StackPair {
		PalletIndex: number;
		StackIndex: number;
	}

	const app = useContext(ApplicationContext) as Application;
	const { all_configs } = useContext(ConfigurationsContext) as any;
	const palletConfig = all_configs as PalletConfigObj;

	const [hasSlipSheets, setHasSlipSheets] = useState<boolean>(false);
	useEffect(() => {
		app.api.Machine.get()
			.then((mc: MachineConfig) => {
				if (!!mc) {
					const hasSS =
						mc.Bays.filter(b => b.SlipSheetMags.filter(m => m).length > 0).length > 0;
					setHasSlipSheets(hasSS);
				}
			})
			.catch(console.log);
	}, []);

	const [currentStep, setCurrentStep] = useState<StackStep>(StackStep.Summary);
	const [triggerConfirm, confirmProps] = useConfirm([currentStep]);

	const {
		stack: editingStack,
		editingMode,
		mode: currentMode,
		palletIndex,
		stackIndex,
		startingAutoParams,
		startEdit,
		addLayer,
		removeLayer,
		setLayout,
		setTransform,
		setSlipSheetPresence,
		setSlipSheetType,
		updateName,
		updateFromAutoParams,
		setMode,
	} = useStackEditor({ allPallets, allSlipSheets });

	interface _StackPair {
		PalletIndex: number;
		StackIndex: number;
	}

	const allStackPairs = useMemo<_StackPair[]>(() => {
		return allPallets.flatMap((pallet, pIndex) =>
			pallet.Stack.map((_, sIndex) => ({
				PalletIndex: pIndex,
				StackIndex: sIndex,
			}))
		);
	}, [allPallets]);

	const checkForStack = () => allStackPairs.length > 0;

	const updatePallets = () => {
		const newPallets = [...allPallets];
		const newStack = { ...editingStack };

		for (let i = 0; i < newStack.layers.length; i++) {
			const layoutIndex = newStack.layers[i].index;
			newStack.layers[i].layout_id =
				newPallets[palletIndex].Layouts[layoutIndex].id;
		}

		if (currentMode === StackMode.Auto && editingMode === 'new') {
			const match = /^Stack (\d+)/.exec(newStack.name);
			const layoutName =
				newPallets[palletIndex].Layouts[newStack.layers[0].index]?.name;
			if (match && layoutName) {
				newStack.name = `${newStack.name} - ${layoutName}`;
			}
		}

		if (stackIndex < 0) {
			newPallets[palletIndex].Stack.push(newStack);
		} else {
			newPallets[palletIndex].Stack[stackIndex] = newStack;
		}

		for (const pallet of newPallets) {
			pallet.Stack = pallet.Stack.filter(s => s.layers.length > 0);
		}

		setPallets(newPallets);
	};

	const stackNameHandlers = getChangeWrapperString((newName: string) => {
		updateName(newName);
	});

	const delete_stack = (palletIndex: number, stackIndex: number) =>
		triggerConfirm(() => {
			const newPallets = [...allPallets];
			newPallets[palletIndex].Stack.splice(stackIndex, 1);
			setPallets(newPallets);
		}, `Are you sure you want to delete ${allPallets[palletIndex].Stack[stackIndex].name}?`);

	const LeftButton: ButtonProps = {
		name: currentStep === StackStep.Summary ? '' : 'Back',
		action: () => {
			if (currentStep === StackStep.Summary) handleBack();
			setCurrentStep(StackStep.Summary);
		},
		enabled: currentStep !== StackStep.Summary,
	};

	function stackNameUnique() {
		for (let i = 0; i < allPallets.length; i++) {
			const pallet = allPallets[i];
			for (let l = 0; l < pallet.Stack.length; l++) {
				if (i !== palletIndex || l !== stackIndex) {
					if (
						pallet.Stack[l].name.toLowerCase() === editingStack.name.toLowerCase()
					) {
						return false;
					}
				}
			}
		}
		return true;
	}

	const RightButton: ButtonProps = {
		name: currentStep === StackStep.Summary ? 'View Summary' : 'Save',
		action: () => {
			if (currentStep === StackStep.Create) {
				updatePallets();
				setCurrentStep(StackStep.Summary);
				return;
			}
			if (checkForStack()) handleNext();
		},
		restrictionCheck: () => {
			if (!stackNameUnique()) return 'Stack name must be unique.';
		},
		enabled:
			currentStep === StackStep.Create
				? editingStack.layers.length > 0
				: checkForStack(),
	};

	let instruction: string = 'Create And Edit Stacks';

	const contentItemProps: ContentItemProps = {
		instructionNumber,
		instruction,
		LeftButton,
		RightButton,
		sub_step: 1,
		sub_step_count: 3,
		isSummary: currentStep === StackStep.Summary,
	};

	const handleStartEdit = (pi: number, si: number) => {
		let targetPalletIndex = pi;

		if (targetPalletIndex < 0) {
			for (let i = 0; i < allPallets.length; i++) {
				if (allPallets[i].Layouts.length > 0) {
					targetPalletIndex = i;
					break;
				}
			}
		}

		// Abort if no valid pallet found
		if (targetPalletIndex < 0) return;

		startEdit(targetPalletIndex, si);
		setCurrentStep(StackStep.Create);
	};

	const update_pallet_index = (index: number) => {
		if (index === palletIndex) return;
		if (allPallets[index].Layouts.length === 0) return;
		startEdit(index, -1);
	};

	const updateAutoStackParams = (params: AutoStackParams) => {
		updateFromAutoParams(params);
	};

	const lToggleStackMode = () => {
		const newMode =
			currentMode === StackMode.Manual ? StackMode.Auto : StackMode.Manual;

		if (newMode !== StackMode.Auto) {
			if (!startingAutoParams) {
				const defaultParams = createDefaultAutoStackParams(
					palletIndex,
					editingStack.layers.length,
					allPallets
				);
				updateFromAutoParams(defaultParams);
			}
		} else {
			if (
				editingStack.slipSheet?.config === SlipSheetOption.Custom &&
				!!startingAutoParams
			) {
				updateFromAutoParams({
					...startingAutoParams,
					slipSheet: editingStack.slipSheet,
					layerCount: editingStack.layers.length,
				});
			} else if (!!startingAutoParams) {
				updateFromAutoParams({
					...startingAutoParams,
					layerCount: editingStack.layers.length,
				});
			}
		}

		setMode(newMode);
	};

	const getVisualizerPallets = () => {
		const palletCopy = { ...allPallets[palletIndex] };
		palletCopy.Stack = [editingStack];
		return [palletCopy];
	};

	switch (currentStep) {
		case StackStep.Create:
			contentItemProps.instruction = 'Create And Configure Layers';

			const manualStackProps: ManualStackProps = {
				allPallets,
				allSlipSheets,
				palletIndex,
				addLayer,
				setLayoutOnLayer: setLayout,
				setTransformOnLayer: setTransform,
				removeLayer,
				editingStack,
				setSlipSheetPresentOnLayer: setSlipSheetPresence,
				setSlipSheetTypeOnLayer: setSlipSheetType,
				updatePalletIndex: update_pallet_index,
				triggerSelect,
				slipsheetEnabled: hasSlipSheets && allSlipSheets.length > 0,
			};

			const visualizerProps: VisualizerProps = {
				palletConfig: {
					name: palletConfig.config.name,
					boxes: allBoxes,
					slipsheets: allSlipSheets,
					pallets: getVisualizerPallets(),
				},
				pConfig: {
					...palletConfig,
					config: {
						...palletConfig.config,
						boxes: allBoxes,
						slipsheets: allSlipSheets,
						pallets: getVisualizerPallets(),
					} as PalletConfiguration,
				} as PalletConfigObj,
				pallet_index: 0,
				stack_index: 0,
				max_boxes: 1000,
			};

			const autoStackProps: AutoStackProps = {
				allPallets,
				allSlipSheets,
				palletIndex,
				onAutoStackParamsChange: updateAutoStackParams,
				initialLayerCount: editingStack.layers.length,
				initialAutoStackParams: startingAutoParams,
				onKeyboardTrigger: triggerKeyboard,
				onSelectTrigger: triggerSelect,
				slipsheetEnabled: hasSlipSheets || false,
			};

			const currentStackNameHandler = stackNameHandlers;
			const currentStackName = editingStack.name;
			const triggerKeyboardName = () => {
				triggerKeyboard?.(
					'Enter a name for your Stack:',
					currentStackName,
					currentStackNameHandler.Direct
				);
			};

			return (
				<ContentItem {...contentItemProps}>
					<div className="full-height full-width grid-col-50">
						<div className="full-size flex-start align-end flex-col">
							<div className="full-width vh-91 rounded grid-row-10-90">
								<div className="full-size flex-col margin-top">
									<ContentFooter
										RightButton={RightButton}
										LeftButton={LeftButton}
										confirmAction={confirmProps}
									/>
								</div>
								<div className="full-width grid-row-70">
									{visualizerProps.palletConfig && (
										<StackScene
											palletConfig={visualizerProps.pConfig}
											stackIndex={visualizerProps.stack_index}
											palletIndex={visualizerProps.pallet_index}
											bayIndex={-1}
											useHighlights={false}
											zoom={1.8}
										/>
									)}
									<StackStats
										allBoxes={allBoxes}
										allSlipSheets={allSlipSheets}
										maxBoxes={visualizerProps.max_boxes}
										allPallets={allPallets}
										editingStack={editingStack}
										palletIndex={palletIndex}
										unitsSystem={unitsSystem}
									/>
								</div>
							</div>
						</div>
						<Scroll>
							<div className="full-size flex-col align-center border-left color-blue">
								<div className="full-width-minus-2pad flex-col align-start double-margin-top double-margin-bottom">
									<span className="bold-font margin-bottom">Stack Name:</span>
									<input
										className="width-100"
										type="text"
										value={currentStackName}
										onChange={currentStackNameHandler.Event}
										onClick={triggerKeyboardName}
									/>
								</div>

								<TargetPallet
									allPallets={allPallets}
									palletIndex={palletIndex}
									selectTargetPallet={update_pallet_index}
									triggerSelect={triggerSelect}
								/>

								<div className="full-width-minus-2pad flex-col align-center">
									<div
										className="double-margin-top full-width flex-row rounded inner-border"
										onClick={lToggleStackMode}
									>
										<div
											className={
												'width-50 full-height flex-row align-center rounded flex-center' +
												` ${
													currentMode === StackMode.Auto
														? 'background-medium-blue color-white'
														: 'color-blue'
												}`
											}
										>
											<span className="bold-font padding">Simple Stack</span>
										</div>
										<div
											className={
												'width-50 full-height flex-row align-center rounded flex-center' +
												` ${
													currentMode === StackMode.Manual
														? 'background-medium-blue color-white'
														: 'color-blue'
												}`
											}
										>
											<span className="bold-font padding">Advanced Stack</span>
										</div>
									</div>
								</div>

								<div className="full-width-minus-2pad flex-center align-center background-desert-storm">
									{currentMode === StackMode.Manual ? (
										<ManualStack {...manualStackProps} />
									) : (
										<AutoStack {...autoStackProps} />
									)}
								</div>
							</div>
						</Scroll>
					</div>
				</ContentItem>
			);

		case StackStep.Summary:
			contentItemProps.AddButton = {
				name: 'Add new stack',
				action: () => {
					handleStartEdit(-1, -1);
					setCurrentStep(StackStep.Create);
				},
			};
			contentItemProps.confirmAction = confirmProps;

			const itemIterator: ItemIterator = {
				pGet: (start, count, filter) => {
					let stacks = [...allStackPairs];
					if (filter) {
						const regex = new RegExp(filter);
						stacks = stacks.filter(({ PalletIndex, StackIndex }) => {
							const pallet = allPallets[PalletIndex];
							const stack = pallet.Stack[StackIndex];
							const layout = pallet.Layouts[stack.layers[0].index];
							const box = allBoxes[layout.boxPositions[0].index];
							return (
								regex.test(stack.name) ||
								regex.test(pallet.name) ||
								regex.test(layout.name) ||
								regex.test(box.name)
							);
						});
					}
					const total = stacks.length;
					const items = stacks
						.splice(start, count)
						.map(({ PalletIndex, StackIndex }, i) => (
							<StackCell
								key={i}
								palletIndex={PalletIndex}
								stackIndex={StackIndex}
								pallet={allPallets[PalletIndex]}
								unitsSystem={unitsSystem}
								boxes={allBoxes}
								deleteStack={() => delete_stack(PalletIndex, StackIndex)}
								startEdit={() => {
									handleStartEdit(PalletIndex, StackIndex);
									setCurrentStep(StackStep.Create);
								}}
								duplicateStack={duplicateConfiguration(
									StackIndex,
									allPallets[PalletIndex].Stack,
									(newStacks: StackObject[]) => {
										const newPallets = [...allPallets];
										newPallets[PalletIndex].Stack = newStacks;
										setPallets(newPallets);
									}
								)}
							/>
						));
					return [items, total];
				},
				triggerKeyboard,
			};

			return (
				<div className="full-size flex-center align-center">
					<SummaryScroll
						{...itemIterator}
						instruction={instruction}
						AddButton={contentItemProps.AddButton}
						RightFooterButton={RightButton}
						LeftFooterButton={LeftButton}
						ConfirmFooter={confirmProps}
					/>
				</div>
			);
	}
}

function TargetPallet({
	palletIndex,
	allPallets,
	selectTargetPallet,
	triggerSelect,
}: TargetPalletProps) {
	const lTriggerSelect = function (event: React.SyntheticEvent) {
		if (!triggerSelect) return;
		event.preventDefault();
		triggerSelect(
			'Select Pallet Filter',
			allPallets.map(pallet => pallet.name),
			selectTargetPallet,
			palletIndex
		);
	};
	return (
		<div className="full-width-minus-2pad flex-col align-start  double-margin-bottom">
			<span className="bold-font margin-bottom">Filter Pallet:</span>

			<select
				className="width-100"
				value={palletIndex}
				onChange={wrapChangeEventNumber(selectTargetPallet)}
				onMouseDown={lTriggerSelect}
			>
				{allPallets.map((p: Pallet_t, i: number) => {
					return (
						<option key={i} value={i}>
							{p.name}
						</option>
					);
				})}
			</select>
		</div>
	);
}

function StackCell({
	pallet,
	startEdit,
	stackIndex,
	deleteStack,
	palletIndex,
	unitsSystem,
	boxes,
	duplicateStack,
}: StackCellProps) {
	const stack = pallet.Stack[stackIndex];
	const weight = stack.layers
		.map(l =>
			(pallet.Layouts[l.index]
				? pallet.Layouts[l.index].boxPositions.map(bp =>
						boxes ? boxes[bp.index].weight || 0 : 0
				  )
				: []
			).reduce((acc, cur) => acc + cur, 0)
		)
		.reduce((acc, cur) => acc + cur, 0);
	const height =
		stack.layers
			.map(l =>
				(pallet.Layouts[l.index]
					? pallet.Layouts[l.index].boxPositions.map(bp =>
							boxes ? boxes[bp.index].dimensions.height || 0 : 0
					  )
					: []
				).reduce((acc, cur) => Math.max(acc, cur), 0)
			)
			.reduce((acc, cur) => acc + cur, 0) + pallet.Dimensions.height;

	let properties =
		`${stack.layers.length} Layer${stack.layers.length > 1 ? 's' : ''}, ` +
		`${render_weight(weight, unitsSystem, true)}, ` +
		`${render_length(height, unitsSystem, true)} (${pallet.name})`;

	function lDeleteStack() {
		deleteStack(palletIndex, stackIndex);
	}

	return (
		<SummaryItemCell
			name={stack.name}
			properties={properties}
			trashAction={lDeleteStack}
			editAction={startEdit}
			duplicateAction={duplicateStack}
		>
			<img src={LayerImage} />
		</SummaryItemCell>
	);
}
